// Auth Entra côté WEB (react-native-web / webpack) : OAuth2 Authorization Code
// + PKCE dans le navigateur, identique au client SPA cc-attack.
// Résolu par webpack via l'extension .web.ts (le module natif .ts n'est pas chargé sur web).

import { AUTHORITY, SCOPES, decodeJwt, getEntraConfig, type EntraClaims } from './entraConfig';
import { resolveApiBase } from '../config/env';

export interface Session {
  accessToken: string;
  expiresAt: number; // epoch seconds
  claims: EntraClaims;
  refreshToken?: string; // si offline_access -> permet le refresh silencieux
}

const PKCE_KEY = 'ccjaw.pkce';
const SESSION_KEY = 'ccjaw.session';
const RETURN_TO_KEY = 'ccjaw.returnTo';

/** Mémorise la page visée (URL relative) à restaurer après authentification. */
export function rememberReturnTo(path: string): void {
  if (typeof window === 'undefined' || !path) return;
  // Ne pas mémoriser les pages d'auth elles-mêmes.
  if (path.startsWith('/auth/callback') || path.startsWith('/logout/callback')) return;
  sessionStorage.setItem(RETURN_TO_KEY, path);
}
/** Lit et efface la page visée. */
export function consumeReturnTo(): string | null {
  if (typeof window === 'undefined') return null;
  const v = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return v;
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function randomString(len: number): string {
  const a = new Uint8Array(len);
  crypto.getRandomValues(a);
  return b64url(a);
}
async function sha256b64url(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return b64url(new Uint8Array(digest));
}

export async function startLogin(signup = false, returnTo?: string): Promise<void> {
  const cfg = getEntraConfig();
  // returnTo explicite -> enregistré ; sinon on garde celui déjà mémorisé (guard).
  if (returnTo) rememberReturnTo(returnTo);
  const verifier = randomString(48);
  const state = randomString(16);
  const challenge = await sha256b64url(verifier);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));

  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: 'code',
    redirect_uri: cfg.redirectUri,
    response_mode: 'query',
    scope: SCOPES.join(' '),
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  if (signup) params.set('prompt', 'create');
  window.location.assign(`${AUTHORITY}/oauth2/v2.0/authorize?${params.toString()}`);
}

export async function completeLoginIfCallback(): Promise<Session | null> {
  if (typeof window === 'undefined' || window.location.pathname !== '/auth/callback') {
    // Nettoyage du retour de logout.
    if (typeof window !== 'undefined' && window.location.pathname === '/logout/callback') {
      sessionStorage.removeItem(SESSION_KEY);
      window.history.replaceState({}, document.title, '/');
    }
    return null;
  }
  const qs = new URLSearchParams(window.location.search);
  const code = qs.get('code');
  const err = qs.get('error');
  const cleanup = () => window.history.replaceState({}, document.title, '/');
  if (err) {
    cleanup();
    throw new Error(`${err}: ${qs.get('error_description') || ''}`);
  }
  if (!code) {
    cleanup();
    return null;
  }
  const saved = JSON.parse(sessionStorage.getItem(PKCE_KEY) || '{}');
  if (!saved.verifier || saved.state !== qs.get('state')) {
    cleanup();
    throw new Error('état PKCE invalide');
  }
  const cfg = getEntraConfig();
  const body = new URLSearchParams({
    client_id: cfg.clientId,
    grant_type: 'authorization_code',
    code,
    redirect_uri: cfg.redirectUri,
    code_verifier: saved.verifier,
    scope: SCOPES.join(' '),
  });
  let res: Response;
  try {
    res = await fetch(`${AUTHORITY}/oauth2/v2.0/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (e: any) {
    cleanup();
    throw new Error(
      'Échange du code bloqué (CORS) — ajoute une plateforme SPA avec ' +
        cfg.redirectUri +
        ' sur cc-attack-jaw-spa-react. ' +
        (e?.message || ''),
    );
  }
  const data = await res.json();
  if (!res.ok) {
    cleanup();
    throw new Error(`token: ${data.error} ${data.error_description || ''}`);
  }
  const session: Session = {
    accessToken: data.access_token,
    expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
    claims: decodeJwt(data.access_token),
    refreshToken: data.refresh_token,
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  sessionStorage.removeItem(PKCE_KEY);
  cleanup();
  return session;
}

/** Échange un refresh_token contre un nouvel access token (grant_type=refresh_token). */
async function refreshSession(refreshToken: string): Promise<Session | null> {
  const cfg = getEntraConfig();
  const body = new URLSearchParams({
    client_id: cfg.clientId,
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    scope: SCOPES.join(' '),
  });
  try {
    const res = await fetch(`${AUTHORITY}/oauth2/v2.0/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.access_token) return null;
    const session: Session = {
      accessToken: data.access_token,
      expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
      claims: decodeJwt(data.access_token),
      refreshToken: data.refresh_token || refreshToken, // Entra peut renvoyer un RT tournant
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  } catch {
    return null;
  }
}

/** Rafraîchit une session Google (HS256 maison) via le backend. */
async function refreshGoogleSession(refreshToken: string): Promise<Session | null> {
  try {
    const res = await fetch(`${resolveApiBase()}/auth/session/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.access_token) return null;
    const session: Session & { kind?: string } = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token || refreshToken,
      expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
      claims: decodeJwt(data.access_token),
      kind: 'google',
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  } catch {
    return null;
  }
}

export async function restoreSession(): Promise<Session | null> {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  const s: Session & { kind?: string } = JSON.parse(raw);
  const now = Math.floor(Date.now() / 1000);
  // Access token encore valide (marge 60s) -> on le garde.
  if (s.expiresAt && s.expiresAt - 60 > now) return s;
  // Session Google (HS256) expirée -> refresh via le backend.
  if (s.kind === 'google') {
    if (s.refreshToken) {
      const g = await refreshGoogleSession(s.refreshToken);
      if (g) return g;
    }
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
  // Expiré (ou proche) : refresh SILENCIEUX si refresh_token dispo -> pas de
  // ré-authentification au simple rechargement de page.
  if (s.refreshToken) {
    const refreshed = await refreshSession(s.refreshToken);
    if (refreshed) return refreshed;
  }
  sessionStorage.removeItem(SESSION_KEY);
  return null;
}

export async function logout(): Promise<void> {
  const cfg = getEntraConfig();
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(PKCE_KEY);
  const params = new URLSearchParams({ post_logout_redirect_uri: cfg.postLogoutUri });
  window.location.assign(`${AUTHORITY}/oauth2/v2.0/logout?${params.toString()}`);
}
